// P2 OD-IMAGES (SD-O76, Adeel ruling 2026-10-06: the grid must show its pictures with no signal, never broken images).
// The grid's thumbnails are signed URLs on the private `photos` bucket (TTL 1 h), so the saved `photos` rows point at
// images the phone may no longer be able to fetch. On every live `photos` write-through the app asks the service worker
// to precache the thumbnails of those (≤ 50) photos under a synthetic key /__thumb/<photoId> — keyed by PHOTO ID, never
// by the signed URL, so a re-signed URL still hits — in a per-farmer cache agxl-thumbs-<farmerId> that is cleared with
// the farmer's snapshot on logout. Full-size images stay online-only. Nothing here logs a signed URL: ids only.

export const THUMB_PATH = '/__thumb/';
export const THUMB_CACHE_PREFIX = 'agxl-thumbs-';
export const THUMB_MAX = 50;

export interface ThumbItem {
  photoId: string;
  url: string;
}
export interface PrecacheThumbsMessage {
  type: 'PRECACHE_THUMBS';
  data: { farmerId: string; items: ThumbItem[] };
}
export interface ClearThumbsMessage {
  type: 'CLEAR_THUMBS';
  data: { farmerId: string };
}

export type TileSource = 'thumb' | 'live' | 'placeholder';

/** The grid tile's source order: cached first with no signal, live first with signal, the placeholder last. */
export function tileSourceOrder(preferCached: boolean, liveUrl?: string | null): TileSource[] {
  const live: TileSource[] = liveUrl ? ['live'] : [];
  return preferCached ? ['thumb', ...live, 'placeholder'] : [...live, 'thumb', 'placeholder'];
}

/** The tile's cached source for a photo — same origin, no secret in it. */
export function thumbUrl(photoId: string): string {
  return `${THUMB_PATH}${encodeURIComponent(photoId)}`;
}

/** ≤ 50 items, thumbnail URL first (the original when there is none), rows without an http(s) URL skipped. */
export function buildPrecacheThumbsMessage(
  farmerId: string,
  photos: ReadonlyArray<{ id: string; thumbnailUrl?: string | null; url?: string | null }>,
): PrecacheThumbsMessage {
  const items: ThumbItem[] = [];
  for (const p of photos) {
    if (items.length >= THUMB_MAX) break;
    const url = p.thumbnailUrl || p.url || '';
    if (!p.id || !/^https?:\/\//.test(url)) continue;
    items.push({ photoId: p.id, url });
  }
  return { type: 'PRECACHE_THUMBS', data: { farmerId, items } };
}

export function buildClearThumbsMessage(farmerId: string): ClearThumbsMessage {
  return { type: 'CLEAR_THUMBS', data: { farmerId } };
}

/** What may be logged about a message: short ids and counts — never a URL. */
export function describeThumbsMessage(m: PrecacheThumbsMessage | ClearThumbsMessage): string {
  const farmer = m.data.farmerId.slice(0, 8);
  if (m.type === 'CLEAR_THUMBS') return `CLEAR_THUMBS farmer=${farmer}`;
  return `PRECACHE_THUMBS farmer=${farmer} n=${m.data.items.length} ids=${m.data.items.map((i) => i.photoId.slice(0, 8)).join(',')}`;
}

/** Best effort: the controlling service worker, or nothing (dev server, first load before claim). Never throws. */
function swPost(message: PrecacheThumbsMessage | ClearThumbsMessage): boolean {
  try {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return false;
    const target = navigator.serviceWorker.controller;
    if (!target) return false;
    target.postMessage(message);
    return true;
  } catch {
    return false;
  }
}

export function postPrecacheThumbs(
  farmerId: string | null | undefined,
  photos: ReadonlyArray<{ id: string; thumbnailUrl?: string | null; url?: string | null }>,
): boolean {
  if (!farmerId) return false;
  const m = buildPrecacheThumbsMessage(farmerId, photos);
  if (m.data.items.length === 0) return false;
  return swPost(m);
}

export function postClearThumbs(farmerId: string | null | undefined): boolean {
  if (!farmerId) return false;
  return swPost(buildClearThumbsMessage(farmerId));
}
